import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import { KINGDOMS, QUESTS } from '@yuan-math/content';
import type { LearnerProfile } from '@yuan-math/learning';
import { currentKingdom, nextMainQuest } from '@yuan-math/world';
import { registry } from '../state/LearnerContext.tsx';

/**
 * The door from the ordinary dashboard into the adventure (docs/rpg.md §35).
 *
 * **It is a door and nothing else.** The dashboard behind it is unchanged and
 * stays first-class: teachers, parents, guests and any student who would rather
 * read a list than walk a map use it, and nothing in the RPG gates, replaces or
 * quietly reduces a single thing on it. This adds one card; it removes nothing.
 *
 * The card names *where the student is* rather than repeating what to study —
 * "what to do next" is three inches below it and is the same recommendation the
 * adventure's own Continue button renders, so saying it twice on one screen
 * would be the platform disagreeing with itself in the space of a scroll.
 *
 * Nothing here reads XP, gold or a level to decide anything. The kingdom comes
 * from `currentKingdom`, which asks the recommendation engine where the student's
 * frontier is — so this card and the dashboard's own "what to do next" are drawn
 * from one answer and cannot name two different places. The quest is derived,
 * like every quest anywhere (docs/rpg-implementation-plan.md §0.4).
 */
export function AdventureCallout({ profile, now }: { profile: LearnerProfile; now: number }): JSX.Element | null {
  if (!FEATURES.rpg) return null;

  const character = profile.rpg?.character;
  const here = currentKingdom(KINGDOMS, registry, profile, now);
  const quest = here ? nextMainQuest(QUESTS, here.kingdom.id, profile, now) : undefined;

  return (
    <section className="dashboard-section">
      <Link
        to="/adventure"
        className="card card--pressable adventure-callout"
        aria-label={
          character
            ? `Continue your adventure${here ? ` in ${here.kingdom.name}` : ''}`
            : 'Start your adventure — create your adventurer'
        }
      >
        <span className="adventure-callout__mark" aria-hidden="true">
          {here?.kingdom.emoji ?? '⚔️'}
        </span>
        <span className="adventure-callout__body">
          <span className="adventure-callout__title">
            {character ? 'Continue your adventure' : 'Start your adventure'}
          </span>
          <span className="adventure-callout__line">
            {here ? (
              <>
                You are in <strong>{here.kingdom.name}</strong>
                {quest ? (
                  <>
                    {' '}
                    · your quest is <strong>{quest.quest.title}</strong>
                  </>
                ) : null}
                .
              </>
            ) : (
              'Ten kingdoms over the same mathematics, on one map.'
            )}
          </span>
          <span className="adventure-callout__note">
            {character
              ? 'The same lessons and the same mastery, with a map and a story over them.'
              : 'Pick a class and a look — it takes a minute, and every lesson here is the one you already have.'}
          </span>
        </span>
        <span className="adventure-callout__go" aria-hidden="true">
          →
        </span>
      </Link>
    </section>
  );
}
